<template>
  <div class="vision-scope vision-diagnosis-summary">
    <div class="vision-diagnosis-grid">
      <section
        v-for="section in sections"
        :key="section.key"
        class="vision-diagnosis-panel"
        :style="{ '--diag-color': getMeta(section.diag.result).color }"
      >
        <header class="vision-diagnosis-panel__header">
          <div class="vision-diagnosis-panel__title">
            <v-icon size="18" :color="getMeta(section.diag.result).color">{{ section.icon }}</v-icon>
            <h3>{{ section.title }}</h3>
          </div>
          <span class="vision-diagnosis-panel__badge">
            {{ getMeta(section.diag.result).label }}
          </span>
        </header>

        <div v-if="getLines(section.diag).length" class="vision-diagnosis-lines">
          <div
            v-for="line in getLines(section.diag)"
            :key="`${section.key}-${line.name}`"
            :class="['vision-diagnosis-line', `is-${line.result}`]"
          >
            <div class="vision-diagnosis-line__head">
              <v-icon size="16" :color="getMeta(line.result).color">
                {{ getMeta(line.result).icon }}
              </v-icon>
              <strong>{{ line.name }}</strong>
              <span class="vision-diagnosis-line__status">
                {{ getMeta(line.result).label }}
              </span>
            </div>

            <div class="vision-diagnosis-line__bar" :title="`Intensidade: ${formatRatio(line.ratio)}`">
              <div
                class="vision-diagnosis-line__bar-fill"
                :style="{ width: `${barWidth(line.ratio)}%` }"
              ></div>
            </div>

            <div class="vision-diagnosis-line__metric">
              <span class="vision-eyebrow">Intensidade</span>
              <span class="vision-diagnosis-line__metric-value">{{ formatRatio(line.ratio) }}</span>
            </div>
          </div>
        </div>

        <div v-else class="vision-diagnosis-empty">
          <v-icon size="22" color="#9ca3af">mdi-test-tube-empty</v-icon>
          <span>Sem linhas calculadas para esta leitura.</span>
        </div>
      </section>
    </div>
  </div>
</template>

<script>
import { formatRatio, getResultMeta, getVisionLineResult } from "./visionUtils";

export default {
  name: "VisionDiagnosisSummary",

  props: {
    result: {
      type: Object,
      required: true,
    },
  },

  computed: {
    sections() {
      return [
        {
          key: "rgb",
          title: "Leitura por RGB",
          icon: "mdi-palette-outline",
          diag: this.result.diagRgb || { lines: {}, result: "inconclusivo" },
        },
        {
          key: "luminance",
          title: "Leitura por Luminância",
          icon: "mdi-brightness-6",
          diag: this.result.diagLum || { lines: {}, result: "inconclusivo" },
        },
      ];
    },
  },

  methods: {
    formatRatio,

    getMeta(result) {
      return getResultMeta(result);
    },

    getLines(diag) {
      const lines = diag && diag.lines ? diag.lines : {};
      return Object.keys(lines).map((name) => ({
        name,
        ratio: lines[name].ratio,
        result: getVisionLineResult(lines[name]),
      }));
    },

    barWidth(ratio) {
      const value = Number(ratio);
      if (!isFinite(value) || value <= 0) return 2;
      return Math.max(2, Math.min(100, value * 100));
    },
  },
};
</script>

<style>
@import "./visionDesignTokens.css";
</style>

<style scoped>
.vision-diagnosis-summary {
  display: grid;
  gap: 16px;
}

.vision-diagnosis-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;
}

.vision-diagnosis-panel {
  position: relative;
  background: var(--vision-surface);
  border: 1px solid var(--vision-border);
  border-radius: var(--vision-radius-md);
  padding: 18px 18px 18px 22px;
  overflow: hidden;
  box-shadow: var(--vision-shadow-1);
  transition: transform var(--vision-anim-base) var(--vision-ease),
              box-shadow var(--vision-anim-base) var(--vision-ease);
}

.vision-diagnosis-panel::before {
  content: "";
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 4px;
  background: var(--diag-color);
}

.vision-diagnosis-panel::after {
  content: "";
  position: absolute;
  inset: 0;
  background: var(--diag-color);
  opacity: 0.04;
  pointer-events: none;
}

.vision-diagnosis-panel:hover {
  box-shadow: var(--vision-shadow-2);
}

.vision-diagnosis-panel__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 14px;
  position: relative;
}

.vision-diagnosis-panel__title {
  display: flex;
  align-items: center;
  gap: 8px;
}

.vision-diagnosis-panel__title h3 {
  margin: 0;
  color: var(--vision-ink-1);
  font-size: 15px;
  line-height: 1.2;
  font-weight: 700;
  letter-spacing: -0.1px;
}

.vision-diagnosis-panel__badge {
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  font-size: 11.5px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  border-radius: var(--vision-radius-pill);
  background: var(--diag-color);
  color: #fff;
}

.vision-diagnosis-lines {
  display: flex;
  flex-direction: column;
  gap: 10px;
  position: relative;
}

.vision-diagnosis-line {
  display: grid;
  grid-template-columns: 1fr auto;
  grid-template-rows: auto auto;
  column-gap: 14px;
  row-gap: 6px;
  padding: 10px 12px;
  background: var(--vision-surface);
  border: 1px solid var(--vision-border);
  border-radius: var(--vision-radius-sm);
}

.vision-diagnosis-line__head {
  grid-column: 1 / 2;
  grid-row: 1 / 2;
  display: flex;
  align-items: center;
  gap: 6px;
  color: var(--vision-ink-2);
  font-size: 13.5px;
}

.vision-diagnosis-line__head strong {
  font-weight: 700;
}

.vision-diagnosis-line__status {
  display: inline-flex;
  align-items: center;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  padding: 2px 8px;
  margin-left: 4px;
  border-radius: var(--vision-radius-pill);
  background: var(--vision-success-soft);
  color: var(--vision-success);
}

.vision-diagnosis-line.is-positivo .vision-diagnosis-line__status {
  background: var(--vision-danger-soft);
  color: var(--vision-danger);
}

.vision-diagnosis-line.is-inconclusivo .vision-diagnosis-line__status {
  background: var(--vision-warning-soft);
  color: var(--vision-warning);
}

.vision-diagnosis-line__bar {
  grid-column: 1 / 2;
  grid-row: 2 / 3;
  height: 8px;
  border-radius: var(--vision-radius-pill);
  background: var(--vision-surface-3);
  overflow: hidden;
  align-self: center;
}

.vision-diagnosis-line__bar-fill {
  height: 100%;
  border-radius: var(--vision-radius-pill);
  background: var(--vision-success);
  transition: width var(--vision-anim-slow) var(--vision-ease);
}

.vision-diagnosis-line.is-positivo .vision-diagnosis-line__bar-fill {
  background: var(--vision-danger);
}

.vision-diagnosis-line.is-inconclusivo .vision-diagnosis-line__bar-fill {
  background: var(--vision-warning);
}

.vision-diagnosis-line__metric {
  grid-column: 2 / 3;
  grid-row: 1 / 3;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  justify-content: center;
  gap: 2px;
  min-width: 86px;
}

.vision-diagnosis-line__metric-value {
  font-family: var(--vision-font-mono);
  font-size: 16px;
  font-weight: 700;
  color: var(--vision-ink-1);
  line-height: 1;
}

.vision-diagnosis-empty {
  display: flex;
  align-items: center;
  gap: 8px;
  color: var(--vision-ink-5);
  font-size: 13.5px;
  padding: 12px 0;
}

@media only screen and (max-width: 800px) {
  .vision-diagnosis-grid {
    grid-template-columns: 1fr;
  }
}

@media only screen and (max-width: 480px) {
  .vision-diagnosis-line {
    grid-template-columns: 1fr;
  }

  .vision-diagnosis-line__metric {
    grid-column: 1 / 2;
    grid-row: 3 / 4;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    width: 100%;
  }
}
</style>
